import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FiAlertTriangle,
  FiInfo,
  FiSearch,
  FiArrowLeft,
  FiX,
  FiCheck,
  FiCopy,
  FiChevronDown,
  FiChevronUp,
  FiShield,
  FiExternalLink,
} from "react-icons/fi";
import {
  MdOutlineAccountBalance,
  MdBlock,
  MdCheckCircle,
  MdOutlineAccessTime,
  MdPhone,
} from "react-icons/md";
import {
  TbCurrencyRupee,
  TbAlertOctagon,
  TbShieldCheck,
  TbClockHour4,
  TbCalendarDue,
  TbRefresh,
  TbWallet,
  TbPlus,
} from "react-icons/tb";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import axios from "../../Utils/axiosInstance";
import { useAuth } from "../../Components/Context/AuthContext";
import { assets } from "../../Assets/assets";
import AppLoader from "../../Components/Common/AppLoader";
import Pagination from "../../Components/Common/Pagination";
import BuyerPenaltyPaymentModal from "../../Components/UserSideComponents/BuyerPenalties/BuyerPenaltyPaymentModal";
import BuyerPenaltyInfoModal from "../../Components/UserSideComponents/BuyerPenalties/BuyerPenaltyInfoModal";
import RechargeModal from "../../Components/UserSideComponents/Recharge/RechargeModal";

const TABS = [
  { label: "All", status: "" },
  { label: "Pending", status: "Pending" },
  { label: "Paid History", status: "Paid" },
];

const getPenaltyStatusConfig = (penalty) => {
  if (penalty.paymentStatus === "Paid") {
    return {
      label: "PAID",
      badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dotClass: "bg-emerald-500",
      accentBorder: "border-l-emerald-500",
    };
  }
  if (penalty.paymentStatus === "Waived") {
    return {
      label: "WAIVED",
      badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
      dotClass: "bg-blue-500",
      accentBorder: "border-l-blue-500",
    };
  }
  if (penalty.paymentStatus === "Cancelled") {
    return {
      label: "CANCELLED",
      badgeClass: "bg-gray-100 text-gray-700 border-gray-200",
      dotClass: "bg-gray-400",
      accentBorder: "border-l-gray-400",
    };
  }
  if (penalty.isBlocking) {
    return {
      label: "BLOCKING",
      badgeClass: "bg-red-50 text-red-700 border-red-200",
      dotClass: "bg-red-600 animate-ping-slow",
      accentBorder: "border-l-red-600",
    };
  }
  if (penalty.isPastDue) {
    return {
      label: "OVERDUE",
      badgeClass: "bg-rose-50 text-rose-700 border-rose-200",
      dotClass: "bg-rose-500",
      accentBorder: "border-l-rose-500",
    };
  }
  if (penalty.daysUntilDue <= 1) {
    return {
      label: "URGENT",
      badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
      dotClass: "bg-amber-500",
      accentBorder: "border-l-amber-500",
    };
  }
  return {
    label: "PENDING",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    dotClass: "bg-amber-500",
    accentBorder: "border-l-amber-400",
  };
};

const BuyerPenalties = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const userId = user?._id || user?.id;

  const [activeTab, setActiveTab] = useState("All");
  const [penalties, setPenalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("due_soon");
  const [expandedPenaltyId, setExpandedPenaltyId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // Wallet State
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false);

  // Modals
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentTargetPenalties, setPaymentTargetPenalties] = useState([]);
  const [infoModalOpen, setIsInfoModalOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);

  // Fetch Wallet Balance
  const fetchWallet = useCallback(async () => {
    if (!userId) return;
    try {
      setWalletLoading(true);
      const res = await axios.get(`/wallet/get-wallet-balance/${userId}`);
      if (res.data?.success) {
        setWallet(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load wallet:", err);
    } finally {
      setWalletLoading(false);
    }
  }, [userId]);

  // Fetch Penalties
  const fetchPenalties = useCallback(
    async (currentPage = 1, isManual = false) => {
      if (!userId) return;
      if (isManual) setRefreshing(true);
      else setLoading(true);

      try {
        const currentTabObj = TABS.find((t) => t.label === activeTab);
        let url = `/penalties/user/${userId}?page=${currentPage}&limit=${limit}`;

        if (currentTabObj?.status) {
          url += `&status=${currentTabObj.status}`;
        }

        const res = await axios.get(url);
        if (res.data?.success) {
          const list = res.data.data || [];
          setPenalties(list);
          setPagination(res.data.pagination || null);

          // Deep linking support
          const params = new URLSearchParams(location.search);
          const autoSelectId = params.get("penaltyId") || params.get("highlight");
          if (autoSelectId && list.length > 0) {
            const found = list.find(
              (p) => p._id === autoSelectId || p.uniquePenaltyId === autoSelectId
            );
            if (found) {
              setExpandedPenaltyId(found._id);
              if (found.paymentStatus === "Pending") {
                setSelectedIds([found._id]);
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch buyer penalties:", err);
        toast.error(err.response?.data?.message || "Failed to load penalties");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [userId, activeTab, limit, location.search]
  );

  useEffect(() => {
    fetchWallet();
  }, [fetchWallet]);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
    fetchPenalties(1);
  }, [fetchPenalties]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelectedIds([]);
    fetchPenalties(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Filter and sort display list
  const displayPenalties = useMemo(() => {
    let list = [...penalties];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((p) => {
        const uniqueId = (p.uniquePenaltyId || "").toLowerCase();
        const reason = (p.reason?.title || p.penaltyType || "").toLowerCase();
        const orderNum = (
          p.orderId?.orderNumber ||
          p.orderNumber ||
          ""
        ).toLowerCase();
        return (
          uniqueId.includes(q) || reason.includes(q) || orderNum.includes(q)
        );
      });
    }

    // Sort
    list.sort((a, b) => {
      if (sortBy === "due_soon") {
        const aDue = a.dueCalculation?.paymentDueDate
          ? new Date(a.dueCalculation.paymentDueDate)
          : new Date(a.createdAt);
        const bDue = b.dueCalculation?.paymentDueDate
          ? new Date(b.dueCalculation.paymentDueDate)
          : new Date(b.createdAt);
        return aDue - bDue;
      }
      if (sortBy === "amount_desc") {
        return (b.amounts?.totalAmount || 0) - (a.amounts?.totalAmount || 0);
      }
      if (sortBy === "amount_asc") {
        return (a.amounts?.totalAmount || 0) - (b.amounts?.totalAmount || 0);
      }
      if (sortBy === "newest") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return 0;
    });

    return list;
  }, [penalties, searchQuery, sortBy]);

  // Statistics
  const stats = useMemo(() => {
    const pendingList = penalties.filter(
      (p) => p.paymentStatus === "Pending" || p.paymentStatus === "Failed"
    );
    const paidList = penalties.filter((p) => p.paymentStatus === "Paid");
    const blockingCount = pendingList.filter((p) => p.isBlocking).length;

    const pendingTotal = pendingList.reduce(
      (sum, p) => sum + (p.amounts?.totalAmount || 0),
      0
    );
    const paidTotal = paidList.reduce(
      (sum, p) => sum + (p.amounts?.totalAmount || 0),
      0
    );

    return {
      pendingCount: pendingList.length,
      pendingTotal,
      paidCount: paidList.length,
      paidTotal,
      blockingCount,
      hasBlocking:
        blockingCount > 0 ||
        user?.isNewOrderBlocked ||
        user?.isAppAccessBlocked,
    };
  }, [penalties, user]);

  // Bulk Selection Handlers
  const pendingPenalties = useMemo(() => {
    return displayPenalties.filter(
      (p) => p.paymentStatus === "Pending" || p.paymentStatus === "Failed"
    );
  }, [displayPenalties]);

  const allPendingSelected =
    pendingPenalties.length > 0 &&
    pendingPenalties.every((p) => selectedIds.includes(p._id));

  const toggleSelectAll = () => {
    if (allPendingSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingPenalties.map((p) => p._id));
    }
  };

  const toggleSelectPenalty = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectedPenaltiesList = useMemo(() => {
    return penalties.filter((p) => selectedIds.includes(p._id));
  }, [penalties, selectedIds]);

  const selectedTotalAmount = useMemo(() => {
    return selectedPenaltiesList.reduce(
      (sum, p) => sum + (p.amounts?.totalAmount || 0),
      0
    );
  }, [selectedPenaltiesList]);

  // Pay single penalty
  const handlePaySingle = (penalty) => {
    setPaymentTargetPenalties([penalty]);
    setPaymentModalOpen(true);
  };

  // Pay all selected
  const handlePaySelected = () => {
    if (!selectedPenaltiesList.length) {
      toast.error("Please select at least one penalty to pay");
      return;
    }
    setPaymentTargetPenalties(selectedPenaltiesList);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    setSelectedIds([]);
    fetchPenalties(1);
    fetchWallet();
  };

  const copyToClipboard = (text, label = "ID") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  if (loading && penalties.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center p-6 font-Urbanist">
        <AppLoader fullScreen message="Loading buyer penalties..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-28 md:pb-16 font-Urbanist">
      <div className="2xl:max-w-7xl mx-auto px-3 sm:px-6 md:px-8 py-4 sm:py-6 space-y-5">
        {/* ══════════════════════════════════════════════════════════════════
            HEADER & ACTIONS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-8 h-8 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-primaryColor hover:border-primaryColor/30 transition shadow-xs sm:hidden"
                title="Go back"
              >
                <FiArrowLeft size={16} />
              </button>
              <h1 className="text-xl sm:text-2xl font-black text-primaryColor flex items-center gap-2 tracking-tight">
                <TbAlertOctagon size={26} className="text-primaryColor" />
                Buyer Penalties & Dues
              </h1>
              <button
                type="button"
                onClick={() => setIsInfoModalOpen(true)}
                className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:text-primaryColor hover:bg-primaryColor/10 flex items-center justify-center transition"
                title="Policy & Support Info"
              >
                <FiInfo size={14} />
              </button>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-0.5 max-w-xl">
              Manage order cancellation & OFM allowance dues. Settle pending dues
              instantly via your Wallet to keep requirement posting active.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => {
                fetchPenalties(page, true);
                fetchWallet();
              }}
              disabled={refreshing || loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-700 dark:text-gray-200 hover:border-primaryColor/30 hover:text-primaryColor shadow-xs transition disabled:opacity-50"
            >
              <TbRefresh
                size={16}
                className={refreshing ? "animate-spin text-primaryColor" : ""}
              />
              <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
            </button>

            <button
              type="button"
              onClick={() => setRechargeModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primaryColor text-white text-xs font-bold hover:bg-primaryColor/90 transition shadow-xs"
            >
              <TbPlus size={15} />
              <span>Recharge Wallet</span>
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            BLOCKING / WARNING BANNER
        ══════════════════════════════════════════════════════════════════ */}
        {stats.hasBlocking ? (
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 via-rose-600 to-red-700 p-4 sm:p-5 text-white shadow-lg border border-red-500/30">
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-xs flex items-center justify-center shrink-0">
                  <MdBlock size={22} className="text-white" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-black tracking-tight flex items-center gap-2">
                    Order Posting Privileges Paused
                    <span className="px-2 py-0.5 rounded-full bg-white text-red-700 text-[10px] font-bold uppercase tracking-wider">
                      Action Required
                    </span>
                  </h3>
                  <p className="text-xs sm:text-sm text-red-50 mt-1 max-w-2xl leading-relaxed">
                    You have pending penalty dues. Posting new requirements is
                    temporarily restricted. Settle your pending penalties now
                    using your wallet to restore full access instantly.
                  </p>
                </div>
              </div>

              {stats.pendingTotal > 0 && (
                <div className="flex items-center gap-2.5 shrink-0 self-start md:self-auto">
                  <button
                    type="button"
                    onClick={() => {
                      const pending = displayPenalties.filter(
                        (p) =>
                          p.paymentStatus === "Pending" ||
                          p.paymentStatus === "Failed"
                      );
                      if (pending.length > 0) {
                        setPaymentTargetPenalties(pending);
                        setPaymentModalOpen(true);
                      } else {
                        fetchPenalties(1);
                      }
                    }}
                    className="px-4 py-2 rounded-xl bg-white text-red-700 hover:bg-red-50 font-bold text-xs transition shadow-md flex items-center gap-1.5"
                  >
                    <TbWallet size={16} />
                    <span>
                      Clear All Dues (₹{stats.pendingTotal.toLocaleString("en-IN")})
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-800/50 p-3.5 sm:p-4 flex items-center justify-between gap-3 text-emerald-900 dark:text-emerald-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 flex items-center justify-center shrink-0">
                <TbShieldCheck size={20} />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-bold leading-tight">
                  Account in Good Standing
                </p>
                <p className="text-[11px] text-emerald-700 dark:text-emerald-400">
                  You have no blocking penalties. Requirement posting and order
                  confirmations are fully active.
                </p>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 font-bold text-[10px] uppercase tracking-wider">
              <FiCheck size={12} /> Clear
            </span>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            METRICS CARDS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Card 1: Pending Dues */}
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-xs">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                Pending Dues
              </span>
              <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-950/50 text-amber-600 flex items-center justify-center">
                <TbClockHour4 size={16} />
              </div>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl sm:text-2xl font-black text-amber-600">
                ₹{stats.pendingTotal.toLocaleString("en-IN")}
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1 font-medium">
              {stats.pendingCount} unpaid penalty record(s)
            </p>
          </div>

          {/* Card 2: Wallet Balance */}
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-xs">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                Wallet Balance
              </span>
              <div className="w-7 h-7 rounded-lg bg-primaryColor/10 text-primaryColor flex items-center justify-center">
                <TbWallet size={16} />
              </div>
            </div>
            <div className="flex items-baseline justify-between gap-1">
              <span className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">
                ₹{Number(wallet?.availableBalance || 0).toLocaleString("en-IN")}
              </span>
              <button
                type="button"
                onClick={() => setRechargeModalOpen(true)}
                className="text-[11px] text-primaryColor font-bold hover:underline"
              >
                + Add
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-1 font-medium">
              Available for instant clearance
            </p>
          </div>

          {/* Card 3: Total Paid History */}
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-xs">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                Cleared to Date
              </span>
              <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 flex items-center justify-center">
                <MdCheckCircle size={16} />
              </div>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xl sm:text-2xl font-black text-emerald-600">
                ₹{stats.paidTotal.toLocaleString("en-IN")}
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1 font-medium">
              {stats.paidCount} penalty obligation(s) paid
            </p>
          </div>

          {/* Card 4: Account Status */}
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-xs">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                Posting Status
              </span>
              <div
                className={`w-7 h-7 rounded-lg flex items-center justify-center ${stats.hasBlocking
                    ? "bg-red-50 text-red-600 dark:bg-red-950/50"
                    : "bg-blue-50 text-blue-600 dark:bg-blue-950/50"
                  }`}
              >
                {stats.hasBlocking ? (
                  <MdBlock size={16} />
                ) : (
                  <TbShieldCheck size={16} />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2.5 h-2.5 rounded-full ${stats.hasBlocking ? "bg-red-500 animate-pulse" : "bg-emerald-500"
                  }`}
              />
              <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                {stats.hasBlocking ? "Posting Paused" : "Active & Verified"}
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1 font-medium">
              {stats.hasBlocking
                ? "Clear dues to unpause"
                : "Full requirement posting active"}
            </p>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            CONTROLS: TABS, SEARCH & SORT
        ══════════════════════════════════════════════════════════════════ */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-2">
          {/* Tabs */}
          <div className="flex items-center gap-1.5 p-1 bg-gray-100 dark:bg-gray-800/80 rounded-2xl w-fit">
            {TABS.map((t) => {
              const isActive = activeTab === t.label;
              return (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => setActiveTab(t.label)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${isActive
                      ? "bg-white dark:bg-gray-900 text-primaryColor shadow-xs"
                      : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                    }`}
                >
                  {t.label}
                  {t.label === "Pending" && stats.pendingCount > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px]">
                      {stats.pendingCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search & Sort */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
            {/* Search Input */}
            <div className="relative flex-1 sm:w-64">
              <FiSearch
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search ID, Order # or reason..."
                className="w-full pl-9 pr-8 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-xs text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:border-primaryColor outline-none transition"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-xs text-gray-400 font-semibold whitespace-nowrap hidden sm:inline">
                Sort:
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-auto px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-xs text-gray-700 dark:text-gray-300 font-medium focus:border-primaryColor outline-none transition cursor-pointer"
              >
                <option value="due_soon">Due Date (Earliest First)</option>
                <option value="amount_desc">Amount (High to Low)</option>
                <option value="amount_asc">Amount (Low to High)</option>
                <option value="newest">Created (Newest First)</option>
              </select>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            PENALTIES LIST
        ══════════════════════════════════════════════════════════════════ */}
        {loading ? (
          <div className="py-16 flex flex-col items-center justify-center">
            <AppLoader fullScreen={false} message="Loading penalties..." />
          </div>
        ) : displayPenalties.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-12 text-center flex flex-col items-center justify-center">
            <img
              src={assets.noordervector}
              alt="No Penalties"
              className="w-48 sm:w-60 mb-4 opacity-80"
            />
            <h3 className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-200">
              {searchQuery
                ? "No matching penalties found"
                : activeTab === "Pending"
                  ? "No Pending Dues"
                  : "No Penalty Records Found"}
            </h3>
            <p className="text-xs text-gray-400 mt-1 max-w-sm">
              {searchQuery
                ? `No records matched your search "${searchQuery}". Try modifying your search keywords.`
                : activeTab === "Pending"
                  ? "Great news! You have zero pending dues. Your buyer account is in excellent standing."
                  : "There are no penalties associated with your buyer account at this time."}
            </p>
          </div>
        ) : (
          <div className="space-y-3.5">
            {displayPenalties.map((penalty) => {
              const config = getPenaltyStatusConfig(penalty);
              const isSelected = selectedIds.includes(penalty._id);
              const isExpanded = expandedPenaltyId === penalty._id;
              const isPending =
                penalty.paymentStatus === "Pending" ||
                penalty.paymentStatus === "Failed";

              return (
                <div
                  key={penalty._id}
                  className={`rounded-2xl bg-white dark:bg-gray-900 border transition-all ${isSelected
                      ? "border-primaryColor ring-2 ring-primaryColor/20 shadow-md"
                      : "border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700 shadow-xs"
                    } border-l-4 ${config.accentBorder} overflow-hidden`}
                >
                  <div className="p-4 sm:p-5">
                    {/* Top Row: Checkbox, IDs, Status Badge */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        {isPending && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectPenalty(penalty._id)}
                            className="mt-1 w-4 h-4 rounded text-primaryColor focus:ring-primaryColor cursor-pointer"
                          />
                        )}

                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-xs sm:text-sm font-black text-gray-900 dark:text-white tracking-wide">
                              {penalty.uniquePenaltyId ||
                                penalty._id.slice(-8).toUpperCase()}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                copyToClipboard(
                                  penalty.uniquePenaltyId || penalty._id,
                                  "Penalty ID"
                                )
                              }
                              className="text-gray-400 hover:text-primaryColor transition"
                              title="Copy Penalty ID"
                            >
                              <FiCopy size={13} />
                            </button>

                            {/* Status badge */}
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider border ${config.badgeClass}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${config.dotClass}`}
                              />
                              {config.label}
                            </span>
                          </div>

                          <p className="text-xs text-gray-400 mt-0.5">
                            Issued on:{" "}
                            {new Date(penalty.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="text-right">
                        <span className="text-xs text-gray-400 block">
                          Total Due
                        </span>
                        <span className="text-base sm:text-lg font-black text-gray-900 dark:text-white">
                          ₹
                          {(
                            penalty.amounts?.totalAmount || 0
                          ).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>

                    {/* Middle Row: Reason, Order Ref, Due Date */}
                    <div className="mt-3.5 pt-3.5 border-t border-gray-100 dark:border-gray-800 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                      {/* Reason */}
                      <div>
                        <span className="text-gray-400 block text-[11px]">
                          Reason / Type
                        </span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {penalty.reason?.title ||
                            penalty.penaltyType ||
                            "Buyer Penalty"}
                        </span>
                      </div>

                      {/* Order Number */}
                      <div>
                        <span className="text-gray-400 block text-[11px]">
                          Linked Order
                        </span>
                        {penalty.orderId?.orderNumber || penalty.orderNumber ? (
                          <span className="font-mono font-semibold text-primaryColor">
                            {penalty.orderId?.orderNumber ||
                              penalty.orderNumber}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">
                            Not specified
                          </span>
                        )}
                      </div>

                      {/* Due Date */}
                      <div>
                        <span className="text-gray-400 block text-[11px]">
                          Due Date
                        </span>
                        <span
                          className={`font-semibold ${penalty.isPastDue && isPending
                              ? "text-rose-600 font-bold"
                              : "text-gray-700 dark:text-gray-300"
                            }`}
                        >
                          {penalty.dueCalculation?.paymentDueDate
                            ? new Date(
                              penalty.dueCalculation.paymentDueDate
                            ).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            : "N/A"}
                        </span>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 space-y-3 animate-fadeIn">
                        {/* Breakdown */}
                        <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 text-xs space-y-1.5">
                          <div className="flex justify-between text-gray-500">
                            <span>Base Fine Amount:</span>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">
                              ₹
                              {(
                                penalty.amounts?.calculatedAmount || 0
                              ).toLocaleString("en-IN")}
                            </span>
                          </div>
                          {penalty.amounts?.gstAmount > 0 && (
                            <div className="flex justify-between text-gray-500">
                              <span>GST (18%):</span>
                              <span className="font-semibold text-gray-700 dark:text-gray-300">
                                ₹
                                {(
                                  penalty.amounts?.gstAmount || 0
                                ).toLocaleString("en-IN")}
                              </span>
                            </div>
                          )}
                          {penalty.amounts?.adminFee > 0 && (
                            <div className="flex justify-between text-gray-500">
                              <span>Admin Fee:</span>
                              <span className="font-semibold text-gray-700 dark:text-gray-300">
                                ₹
                                {(
                                  penalty.amounts?.adminFee || 0
                                ).toLocaleString("en-IN")}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between text-xs font-bold text-gray-900 dark:text-white pt-1.5 border-t border-gray-200 dark:border-gray-700">
                            <span>Net Total:</span>
                            <span className="text-primaryColor font-black">
                              ₹
                              {(
                                penalty.amounts?.totalAmount || 0
                              ).toLocaleString("en-IN")}
                            </span>
                          </div>
                        </div>

                        {/* Paid Information if Paid */}
                        {penalty.paymentStatus === "Paid" && (
                          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 p-3 text-xs space-y-1 text-emerald-900 dark:text-emerald-200 border border-emerald-200/60 dark:border-emerald-800/40">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold">
                                Settlement Method:
                              </span>
                              <span>
                                {penalty.paymentMethod === "wallet" ||
                                  penalty.razorpay?.paymentId?.startsWith("WALLET")
                                  ? "Wallet Debit"
                                  : "Online Payment"}
                              </span>
                            </div>
                            {penalty.paidAt && (
                              <div className="flex items-center justify-between">
                                <span className="font-semibold">Settled On:</span>
                                <span>
                                  {new Date(penalty.paidAt).toLocaleString(
                                    "en-IN"
                                  )}
                                </span>
                              </div>
                            )}
                            {penalty.razorpay?.paymentId && (
                              <div className="flex items-center justify-between">
                                <span className="font-semibold">Reference:</span>
                                <span className="font-mono">
                                  {penalty.razorpay.paymentId}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {penalty.reason?.description && (
                          <p className="text-xs text-gray-500 italic bg-gray-50/50 dark:bg-gray-800/30 p-2.5 rounded-lg">
                            "{penalty.reason.description}"
                          </p>
                        )}
                      </div>
                    )}

                    {/* Bottom Actions Row */}
                    <div className="mt-3 pt-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedPenaltyId((prev) =>
                            prev === penalty._id ? null : penalty._id
                          )
                        }
                        className="text-xs text-gray-500 hover:text-primaryColor font-semibold flex items-center gap-1 transition"
                      >
                        <span>{isExpanded ? "Less Details" : "View Breakdown"}</span>
                        {isExpanded ? (
                          <FiChevronUp size={14} />
                        ) : (
                          <FiChevronDown size={14} />
                        )}
                      </button>

                      {isPending && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handlePaySingle(penalty)}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primaryColor text-white text-xs font-bold hover:bg-primaryColor/90 transition shadow-xs"
                          >
                            <TbWallet size={15} />
                            <span>
                              Pay ₹
                              {(
                                penalty.amounts?.totalAmount || 0
                              ).toLocaleString("en-IN")}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            PAGINATION
        ══════════════════════════════════════════════════════════════════ */}
        {pagination && pagination.pages > 1 && (
          <div className="pt-4 flex justify-center">
            <Pagination
              currentPage={page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            STICKY BOTTOM CHECKOUT BAR (WHEN PENALTIES SELECTED)
        ══════════════════════════════════════════════════════════════════ */}
        {selectedIds.length > 0 && (
          <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 py-3.5 px-4 sm:px-8 shadow-2xl animate-slideUp">
            <div className="2xl:max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-primaryColor text-white text-xs font-black flex items-center justify-center">
                    {selectedIds.length}
                  </span>
                  <div>
                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200 block">
                      {selectedIds.length} Penalty Record(s) Selected
                    </span>
                    <span className="text-[11px] text-gray-400">
                      Total:{" "}
                      <strong className="text-primaryColor font-black text-sm">
                        ₹{selectedTotalAmount.toLocaleString("en-IN")}
                      </strong>
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  className="text-xs text-gray-400 hover:text-gray-600 underline sm:ml-2"
                >
                  Clear Selection
                </button>
              </div>

              <div className="flex items-center gap-2.5 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handlePaySelected}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 py-2.5 px-6 rounded-xl bg-primaryColor text-white font-bold text-xs hover:bg-primaryColor/90 transition shadow-md active:scale-98"
                >
                  <TbWallet size={16} />
                  <span>
                    Pay Selected (₹{selectedTotalAmount.toLocaleString("en-IN")})
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            MODALS
        ══════════════════════════════════════════════════════════════════ */}
        <BuyerPenaltyPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          selectedPenalties={paymentTargetPenalties}
          user={user}
          wallet={wallet}
          fetchWallet={fetchWallet}
          onSuccess={handlePaymentSuccess}
        />

        <BuyerPenaltyInfoModal
          isOpen={infoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
        />

        <RechargeModal
          isOpen={rechargeModalOpen}
          onClose={() => setRechargeModalOpen(false)}
          wallet={wallet}
          user={user}
          onSuccess={async () => {
            await fetchWallet();
            setRechargeModalOpen(false);
          }}
        />
      </div>
    </div>
  );
};

export default BuyerPenalties;
